import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Confetti } from "./Confetti";

/*
 * celebrations.js — Duolingo-style animations for Samajh
 *
 * Exports:
 *   <CorrectFeedback />   — green flash + checkmark after correct MCQ
 *   <WrongFeedback />     — shake + red flash after wrong MCQ
 *   <ConceptDone />       — interstitial between concepts (ring fill + text)
 *   <ChapterIntro />      — full-screen chapter start animation
 *   <ChapterDone />       — full-screen celebration with confetti + stats
 *   <StreakCelebration />  — milestone celebration (7/14/30 day streak)
 */

const ff = "'Poppins', system-ui, sans-serif";
const spring = { type: "spring", stiffness: 300, damping: 20 };
const springBouncy = { type: "spring", stiffness: 400, damping: 15 };

// ─── ANIMATED SVG CHECKMARK ──────────────────────────────────────────────────
const AnimatedCheck = ({ size = 48, color = "#1A7A52", delay = 0 }) => (
  <motion.svg width={size} height={size} viewBox="0 0 48 48"
    initial={{ scale: 0, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ ...springBouncy, delay }}
  >
    <motion.circle cx="24" cy="24" r="22" fill={`${color}15`} stroke={color} strokeWidth="2.5"
      initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
      transition={{ duration: 0.4, delay }} />
    <motion.path d="M14 24l7 7 13-13" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
      initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
      transition={{ duration: 0.3, delay: delay + 0.3 }} />
  </motion.svg>
);

// ─── ANIMATED RING FILL ──────────────────────────────────────────────────────
const RingProgress = ({ progress = 1, size = 80, color = "#1A7A52", strokeWidth = 6 }) => {
  const r = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(140,110,60,0.08)" strokeWidth={strokeWidth} />
      <motion.circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={strokeWidth}
        strokeLinecap="round" strokeDasharray={circ}
        initial={{ strokeDashoffset: circ }}
        animate={{ strokeDashoffset: circ * (1 - progress) }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      />
    </svg>
  );
};

// ─── STAR BURST (CSS particles) ──────────────────────────────────────────────
const StarBurst = ({ count = 8, size = 120, color = "#E8A817" }) => (
  <div style={{ position: "absolute", width: size, height: size, pointerEvents: "none" }}>
    {Array.from({ length: count }).map((_, i) => {
      const angle = (360 / count) * i;
      const rad = (angle * Math.PI) / 180;
      return (
        <motion.div key={i}
          initial={{ x: 0, y: 0, scale: 1, opacity: 1 }}
          animate={{
            x: Math.cos(rad) * (size / 2.5),
            y: Math.sin(rad) * (size / 2.5),
            scale: 0, opacity: 0,
          }}
          transition={{ duration: 0.6, delay: i * 0.03, ease: "easeOut" }}
          style={{
            position: "absolute", left: "50%", top: "50%",
            width: 6, height: 6, borderRadius: "50%",
            background: [color, "#1A7A52", "#3B82F6", "#E85D3A"][i % 4],
            marginLeft: -3, marginTop: -3,
          }}
        />
      );
    })}
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// 1. CORRECT ANSWER FEEDBACK
// Shows green flash bar at bottom + animated checkmark
// Auto-dismisses after 1.5s
// ═══════════════════════════════════════════════════════════════════════════════
export function CorrectFeedback({ show, onDone }) {
  useEffect(() => {
    if (show) {
      const t = setTimeout(() => onDone?.(), 1500);
      return () => clearTimeout(t);
    }
  }, [show, onDone]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={spring}
          style={{
            position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 100,
            background: "linear-gradient(135deg, #E8F5EE, #D1F0E4)",
            borderTop: "2px solid #1A7A52",
            padding: "16px 20px", display: "flex", alignItems: "center", gap: 14,
            fontFamily: ff,
          }}
        >
          <AnimatedCheck size={36} />
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#1A7A52" }}>Correct!</div>
            <div style={{ fontSize: 12, color: "#3A5A4A", marginTop: 2 }}>Keep going 💪</div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 2. WRONG ANSWER FEEDBACK
// Shows red flash bar at bottom + shake
// Auto-dismisses after 1.5s
// ═══════════════════════════════════════════════════════════════════════════════
export function WrongFeedback({ show, correctAnswer, onDone }) {
  useEffect(() => {
    if (show) {
      const t = setTimeout(() => onDone?.(), 2000);
      return () => clearTimeout(t);
    }
  }, [show, onDone]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={spring}
          style={{
            position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 100,
            background: "linear-gradient(135deg, #FEF2F2, #FEE2E2)",
            borderTop: "2px solid #E85D3A",
            padding: "16px 20px", display: "flex", alignItems: "center", gap: 14,
            fontFamily: ff,
          }}
        >
          <motion.div
            animate={{ x: [0, -6, 6, -4, 4, 0] }}
            transition={{ duration: 0.4 }}
            style={{
              width: 36, height: 36, borderRadius: "50%",
              background: "#FEE2E2", border: "2px solid #E85D3A",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 18,
            }}
          >✕</motion.div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#B91C1C" }}>Not quite</div>
            {correctAnswer && <div style={{ fontSize: 12, color: "#7A3A3A", marginTop: 2 }}>Correct: {correctAnswer}</div>}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 3. CONCEPT DONE — interstitial between concepts
// Shows for 2.5s with ring fill + concept name + auto-advances
// ═══════════════════════════════════════════════════════════════════════════════
export function ConceptDone({ show, conceptName, conceptIndex, totalConcepts, onDone }) {
  useEffect(() => {
    if (show) {
      const t = setTimeout(() => onDone?.(), 2500);
      return () => clearTimeout(t);
    }
  }, [show, onDone]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          style={{
            position: "fixed", inset: 0, zIndex: 150,
            background: "rgba(255,255,255,0.97)",
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            fontFamily: ff, gap: 16,
          }}
        >
          <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <RingProgress progress={(conceptIndex + 1) / totalConcepts} size={90} />
            <StarBurst count={10} size={140} />
            <motion.div
              initial={{ scale: 0 }} animate={{ scale: 1 }}
              transition={{ ...springBouncy, delay: 0.3 }}
              style={{ position: "absolute", fontSize: 28, fontWeight: 800, color: "#1A7A52" }}
            >✓</motion.div>
          </div>

          <motion.div
            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div style={{ fontSize: 11, fontWeight: 600, color: "#7A7A6A", textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "center" }}>
              Concept {conceptIndex + 1} of {totalConcepts}
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#0A1820", textAlign: "center", marginTop: 6, maxWidth: 260 }}>
              {conceptName}
            </div>
          </motion.div>

          {/* Progress dots */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            style={{ display: "flex", gap: 6, marginTop: 8 }}
          >
            {Array.from({ length: totalConcepts }).map((_, i) => (
              <motion.div key={i}
                initial={i === conceptIndex ? { scale: 0 } : {}}
                animate={i === conceptIndex ? { scale: 1 } : {}}
                transition={springBouncy}
                style={{
                  width: i <= conceptIndex ? 10 : 8,
                  height: i <= conceptIndex ? 10 : 8,
                  borderRadius: "50%",
                  background: i <= conceptIndex ? "#1A7A52" : "rgba(140,110,60,0.12)",
                  transition: "all 0.3s",
                }}
              />
            ))}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 4. CHAPTER INTRO — full-screen opening before slides start
// Subject icon pulse → chapter name springs in → "Let's go!" → fades out
// ═══════════════════════════════════════════════════════════════════════════════
export function ChapterIntro({ show, chapterName, subject, conceptCount, onDone }) {
  const subjectColors = {
    Maths: "#D4880A", Science: "#10B981", Geography: "#3B82F6",
    Economics: "#8B5CF6", History: "#E85D3A", Civics: "#5A7090",
  };
  const color = subjectColors[subject] || "#1A7A52";

  useEffect(() => {
    if (show) {
      const t = setTimeout(() => onDone?.(), 3000);
      return () => clearTimeout(t);
    }
  }, [show, onDone]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.05 }}
          transition={{ duration: 0.4 }}
          style={{
            position: "fixed", inset: 0, zIndex: 200,
            background: "#6AAAC8",
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            fontFamily: ff, padding: 32,
          }}
        >
          {/* Subject icon with pulse */}
          <motion.div
            initial={{ scale: 0, rotate: -20 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ ...springBouncy, delay: 0.2 }}
            style={{
              width: 80, height: 80, borderRadius: 24,
              background: "rgba(255,255,255,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
              marginBottom: 24,
            }}
          >
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 1.5, repeat: 1, ease: "easeInOut" }}
              style={{ fontSize: 36 }}
            >
              {subject === "Maths" ? "📐" : subject === "Science" ? "🔬" : subject === "Geography" ? "🌍" : subject === "Economics" ? "📊" : "📚"}
            </motion.div>
          </motion.div>

          {/* Subject label */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            style={{ fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.7)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}
          >{subject}</motion.div>

          {/* Chapter name */}
          <motion.div
            initial={{ y: 30, opacity: 0, scale: 0.9 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            transition={{ ...spring, delay: 0.6 }}
            style={{ fontSize: 28, fontWeight: 800, color: "white", textAlign: "center", letterSpacing: "-0.02em", lineHeight: 1.2, maxWidth: 300 }}
          >{chapterName}</motion.div>

          {/* Concept count */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.0 }}
            style={{ fontSize: 14, color: "rgba(255,255,255,0.6)", marginTop: 12 }}
          >{conceptCount} concepts · ~{Math.max(5, conceptCount * 4)} min</motion.div>

          {/* Let's go pill */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.4 }}
            onClick={() => onDone?.()}
            style={{
              marginTop: 32, padding: "14px 40px", borderRadius: 16,
              background: "white", color: "#1A7A52",
              fontSize: 16, fontWeight: 700, cursor: "pointer",
              boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
            }}
          >Let's go →</motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 5. CHAPTER DONE — full celebration screen with confetti
// Confetti fires → trophy/checkmark → stats animate in → CTAs
// ═══════════════════════════════════════════════════════════════════════════════
export function ChapterDone({ show, chapterName, stats, onNext, onHome }) {
  const [confettiKey, setConfettiKey] = useState(0);

  useEffect(() => {
    if (show) setConfettiKey(k => k + 1);
  }, [show]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: "fixed", inset: 0, zIndex: 200,
            background: "linear-gradient(180deg, #E8F5EE 0%, #F0FAF7 50%, white 100%)",
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            fontFamily: ff, padding: 32, overflow: "hidden",
          }}
        >
          <Confetti trigger={confettiKey} />

          {/* Trophy / big checkmark */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ ...springBouncy, delay: 0.3 }}
            style={{ position: "relative", marginBottom: 24 }}
          >
            <div style={{
              width: 100, height: 100, borderRadius: 30,
              background: "linear-gradient(135deg, #1A7A52, #228B5A)",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 8px 32px rgba(26,122,82,0.3)",
            }}>
              <motion.div
                initial={{ scale: 0 }} animate={{ scale: 1 }}
                transition={{ ...springBouncy, delay: 0.6 }}
                style={{ fontSize: 48 }}
              >🎉</motion.div>
            </div>
            <StarBurst count={12} size={180} color="#E8A817" />
          </motion.div>

          {/* Chapter complete text */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            style={{ fontSize: 13, fontWeight: 600, color: "#7A7A6A", textTransform: "uppercase", letterSpacing: "0.06em" }}
          >Chapter complete</motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.7 }}
            style={{ fontSize: 24, fontWeight: 800, color: "#0A1820", textAlign: "center", marginTop: 8, maxWidth: 280 }}
          >{chapterName}</motion.div>

          {/* Stats row */}
          {stats && (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.0 }}
              style={{ display: "flex", gap: 16, marginTop: 24 }}
            >
              {[
                { value: stats.concepts || 0, label: "Concepts" },
                { value: stats.quizScore ? `${stats.quizScore}%` : "—", label: "Quiz" },
                { value: stats.time || "—", label: "Time" },
              ].map((s, i) => (
                <motion.div key={i}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 1.0 + i * 0.15 }}
                  style={{
                    background: "rgba(255,255,255,0.9)", borderRadius: 14,
                    padding: "14px 20px", textAlign: "center", minWidth: 80,
                    border: "1px solid rgba(140,110,60,0.06)",
                    boxShadow: "0 2px 8px rgba(140,110,60,0.08)",
                  }}
                >
                  <div style={{ fontSize: 20, fontWeight: 800, color: "#1A7A52" }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: "#7A7A6A", marginTop: 2 }}>{s.label}</div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* CTAs */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.4 }}
            style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 32, width: "100%", maxWidth: 280 }}
          >
            {onNext && (
              <button onClick={onNext} style={{
                width: "100%", padding: "15px", borderRadius: 14,
                background: "linear-gradient(135deg, #1A7A52, #228B5A)", color: "white",
                border: "none", fontSize: 16, fontWeight: 700, cursor: "pointer",
                fontFamily: ff, boxShadow: "0 4px 16px rgba(26,122,82,0.25)",
              }}>Continue to next chapter →</button>
            )}
            <button onClick={onHome} style={{
              width: "100%", padding: "14px", borderRadius: 14,
              background: "white", color: "#5A6A60",
              border: "1px solid rgba(140,110,60,0.1)", fontSize: 14, fontWeight: 600,
              cursor: "pointer", fontFamily: ff,
            }}>Back to chapters</button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 6. STREAK CELEBRATION — milestone (7/14/30 days)
// Fire animation + streak count + auto-dismiss
// ═══════════════════════════════════════════════════════════════════════════════
export function StreakCelebration({ show, days, onDone }) {
  useEffect(() => {
    if (show) {
      const t = setTimeout(() => onDone?.(), 3500);
      return () => clearTimeout(t);
    }
  }, [show, onDone]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => onDone?.()}
          style={{
            position: "fixed", inset: 0, zIndex: 250,
            background: "linear-gradient(180deg, #FFF8E1 0%, #FFF3CC 50%, #FFFBEB 100%)",
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            fontFamily: ff, cursor: "pointer",
          }}
        >
          {/* Fire animation */}
          <motion.div
            initial={{ scale: 0, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            transition={springBouncy}
            style={{ fontSize: 64, marginBottom: 16 }}
          >🔥</motion.div>

          <StarBurst count={12} size={200} color="#E8A817" />

          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ ...spring, delay: 0.3 }}
            style={{ fontSize: 48, fontWeight: 800, color: "#B07800" }}
          >{days}</motion.div>

          <motion.div
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            style={{ fontSize: 18, fontWeight: 600, color: "#7A5200", marginTop: 4 }}
          >day streak!</motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            style={{ fontSize: 13, color: "#A08A50", marginTop: 20 }}
          >Tap to continue</motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
